import { makeAutoObservable, observable, runInAction } from 'mobx';
import type { RootStore } from './root-store';

export interface Platform {
  id: number;
  name: string;
  language: string;
  timezone: string;
  services: Record<string, any>;
  [key: string]: any;
}

/**
 * PlatformStore — управление платформами отеля
 * Заменяет store/platform-info + modules/redux/platform*
 */
export class PlatformStore {
  items = observable.map<number, Platform>();
  activePlatformId: number | null = null;
  superadminEnterMode: boolean = false;

  private root: RootStore;

  constructor(root: RootStore) {
    this.root = root;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get activePlatform(): Platform | undefined {
    return (
      this.items.get(this.activePlatformId!) ??
      this.items.get(this.root.profile?.platform)
    );
  }

  get platformList(): Platform[] {
    return Array.from(this.items.values());
  }

  setActivePlatform(id: number) {
    this.activePlatformId = id;
  }

  enterPlatform(id: number, data: any) {
    const platformData: Platform = {
      ...data,
      id,
      name: data.short_name || data.name || `Platform #${id}`,
      language: data.default_language || data.language || 'ru',
      timezone: data.tz || data.timezone || 'Europe/Moscow',
      services: Array.isArray(data.services) ? data.services : [],
    };
    this.items.set(id, platformData);
    this.activePlatformId = id;
    this.superadminEnterMode = true;
  }

  exitPlatform() {
    this.superadminEnterMode = false;
  }

  setFromApi(platforms: any[]) {
    runInAction(() => {
      platforms.forEach((p) => {
        this.items.set(p.id, {
          ...p,
          // Нормализуем services в массив [{name, enabled}] для единообразия
          services: Array.isArray(p.services)
            ? p.services
            : Object.entries(p.services || {}).map(([name, svc]: [string, any]) => ({
                name,
                enabled: svc?.enabled ?? true,
                ...svc,
              })),
        });
      });
    });
  }

  updatePlatform(id: number, data: Partial<Platform>) {
    const existing = this.items.get(id);
    if (existing) {
      this.items.set(id, { ...existing, ...data });
    }
  }

  updatePlatformService(
    platformId: number,
    serviceName: string,
    serviceData: any,
  ) {
    const existing = this.items.get(platformId);
    if (existing) {
      this.items.set(platformId, {
        ...existing,
        services: { ...existing.services, [serviceName]: serviceData },
      });
    }
  }
}
